import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Bug, Camera, Loader2, ShieldCheck, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCreateBugReport, BugReportPriority } from "@/hooks/useBugReport";
import { useUserClinicIds } from "@/hooks/useUserClinics";
import { BugReportKind } from "@/lib/bug-report-context";
import { BugReportPrefill } from "./BugReportProvider";

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: BugReportPrefill;
}

const AREAS = [
  { value: "dashboard", label: "Dashboard" },
  { value: "patients", label: "Patients" },
  { value: "clinical_visits", label: "Clinical Visits" },
  { value: "appointments", label: "Appointments" },
  { value: "prescriptions", label: "Prescriptions" },
  { value: "billing", label: "Billing / Dues" },
  { value: "treatment_plans", label: "Treatment Plans" },
  { value: "inventory", label: "Inventory" },
  { value: "imaging", label: "Imaging / X-rays" },
  { value: "reports", label: "Reports" },
  { value: "runtime", label: "App Error" },
  { value: "other", label: "Other" },
];

const PRIORITIES: Array<{ value: BugReportPriority; label: string; description: string }> = [
  { value: "low", label: "Low", description: "Cosmetic or minor inconvenience" },
  { value: "medium", label: "Medium", description: "Workflow issue with workaround" },
  { value: "high", label: "High", description: "Important workflow blocked" },
  { value: "urgent", label: "Urgent", description: "Data loss, security, or clinic stoppage" },
];

const MAX_SCREENSHOT_SIZE = 5 * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

function buildInitialForm(prefill?: BugReportPrefill) {
  return {
    reportKind: prefill?.reportKind || ("manual" as BugReportKind),
    priority: prefill?.priority || ("medium" as BugReportPriority),
    area: prefill?.area || "other",
    subject: prefill?.subject || "",
    description: prefill?.description || "",
    expectedBehavior: prefill?.expectedBehavior || "",
    stepsToReproduce: prefill?.stepsToReproduce || "",
    clinicId: "",
    sentryEventId: prefill?.sentryEventId || null,
    systemErrorLogId: prefill?.systemErrorLogId || null,
  };
}

export function BugReportDialog({ open, onOpenChange, prefill }: BugReportDialogProps) {
  const createBugReport = useCreateBugReport();
  const { clinics } = useUserClinicIds();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState(() => buildInitialForm(prefill));
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const selectedPriority = useMemo(
    () => PRIORITIES.find((priority) => priority.value === formData.priority),
    [formData.priority]
  );

  useEffect(() => {
    if (!open) return;
    const initial = buildInitialForm(prefill);
    if (clinics.length === 1) {
      initial.clinicId = clinics[0].clinic_id;
    }
    setFormData(initial);
    setScreenshot(null);
    setFileError(null);
  }, [clinics, open, prefill]);

  const handleFileChange = (file: File | null) => {
    setFileError(null);

    if (!file) {
      setScreenshot(null);
      return;
    }

    if (!ALLOWED_SCREENSHOT_TYPES.has(file.type)) {
      setScreenshot(null);
      setFileError("Screenshot must be a PNG, JPG, or WebP image.");
      return;
    }

    if (file.size > MAX_SCREENSHOT_SIZE) {
      setScreenshot(null);
      setFileError("Screenshot must be 5 MB or smaller.");
      return;
    }

    setScreenshot(file);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formData.subject.trim().length < 5 || formData.description.trim().length < 20) {
      return;
    }

    await createBugReport.mutateAsync({
      subject: formData.subject,
      description: formData.description,
      expected_behavior: formData.expectedBehavior,
      steps_to_reproduce: formData.stepsToReproduce,
      priority: formData.priority,
      area: formData.area,
      report_kind: formData.reportKind,
      clinic_id: formData.clinicId || null,
      screenshot,
      sentry_event_id: formData.sentryEventId,
      system_error_log_id: formData.systemErrorLogId,
    });

    onOpenChange(false);
  };

  const canSubmit =
    formData.subject.trim().length >= 5 &&
    formData.description.trim().length >= 20 &&
    !createBugReport.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-primary" />
            Report Bug
          </DialogTitle>
          <DialogDescription>
            Share what happened. Safe technical context from this screen will be attached automatically.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-5" onSubmit={handleSubmit}>
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertTitle>Privacy-safe by default</AlertTitle>
            <AlertDescription>
              DentalFlow does not intentionally capture patient names, phone numbers, emails, prescriptions, bills, or
              clinical notes automatically. Screenshots are optional and user controlled.
            </AlertDescription>
          </Alert>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="bug-priority">Severity</Label>
              <Select
                value={formData.priority}
                onValueChange={(priority: BugReportPriority) =>
                  setFormData((prev) => ({ ...prev, priority }))
                }
              >
                <SelectTrigger id="bug-priority">
                  <SelectValue placeholder="Select severity" />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((priority) => (
                    <SelectItem key={priority.value} value={priority.value}>
                      {priority.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPriority && (
                <p className="text-xs text-muted-foreground">{selectedPriority.description}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-area">Module / Area</Label>
              <Select
                value={formData.area}
                onValueChange={(area) => setFormData((prev) => ({ ...prev, area }))}
              >
                <SelectTrigger id="bug-area">
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  {AREAS.map((area) => (
                    <SelectItem key={area.value} value={area.value}>
                      {area.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-clinic">Clinic</Label>
              <Select
                value={formData.clinicId || "none"}
                onValueChange={(clinicId) =>
                  setFormData((prev) => ({ ...prev, clinicId: clinicId === "none" ? "" : clinicId }))
                }
              >
                <SelectTrigger id="bug-clinic">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No clinic selected</SelectItem>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.clinic_id} value={clinic.clinic_id}>
                      {clinic.clinic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-title">Title</Label>
            <Input
              id="bug-title"
              value={formData.subject}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, subject: event.target.value }))
              }
              placeholder="Short summary of the issue"
              required
              minLength={5}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-description">What happened?</Label>
            <Textarea
              id="bug-description"
              value={formData.description}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, description: event.target.value }))
              }
              placeholder="Describe the problem and what you were trying to do."
              rows={4}
              required
              minLength={20}
            />
            <p className="text-xs text-muted-foreground">Minimum 20 characters.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="bug-expected">Expected behavior</Label>
              <Textarea
                id="bug-expected"
                value={formData.expectedBehavior}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, expectedBehavior: event.target.value }))
                }
                placeholder="What should have happened?"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-steps">Steps to reproduce</Label>
              <Textarea
                id="bug-steps"
                value={formData.stepsToReproduce}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, stepsToReproduce: event.target.value }))
                }
                placeholder="1. Open...\n2. Click...\n3. See..."
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bug-screenshot">Screenshot</Label>
            <div className="flex flex-wrap items-center gap-3">
              <Input
                ref={fileInputRef}
                id="bug-screenshot"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(event) => handleFileChange(event.target.files?.[0] || null)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Camera className="mr-2 h-4 w-4" />
                Choose Screenshot
              </Button>
              {screenshot && (
                <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                  <span className="max-w-[240px] truncate">{screenshot.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    aria-label="Remove screenshot"
                    onClick={() => {
                      setScreenshot(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            {fileError && (
              <p className="flex items-center gap-1 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {fileError}
              </p>
            )}
            <p className="text-xs text-muted-foreground">Optional. PNG, JPG, or WebP up to 5 MB.</p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createBugReport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Report
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
